import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormGroup, Validators, FormControl } from '@angular/forms';

import { BehaviorSubject, Subscription, tap, Subject, filter } from 'rxjs';

import { MessageService } from '@shared/services/message-service';

import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { ShiftStatus } from '@shared/models/worked-shift';
import { CustomValidators } from '@shared/helpers/custom-validators';

import { InventoryService } from '../../../../services/inventory.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { InvCountIgpsInfo } from '../../../../models/inv-count-igps-info';
import { saveDebounce } from '@shared/helpers/save-debounce';

@Component({
    selector: 'app-igps-misc-info',
    templateUrl: './igps-misc-info.component.html',
    styleUrls: ['./igps-misc-info.component.scss']
})
export class IgpsMiscInfoComponent implements OnInit, OnDestroy {

    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    invCountIgpsInfo: InvCountIgpsInfo;

    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();
    form = new FormGroup({
        palletsWashed: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ]),
        noScanPalletsWashed: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false) ])
    });

    siteSubinventoryId = new BehaviorSubject<number>(null);

    constructor(
        private inventoryService: InventoryService,
        private activityService: ActivityBlockingService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService) {
    }

    ngOnInit() {
        const contextObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            if (context.status !== ShiftStatus.Open)
                this.form.disable({ emitEvent: false });
            else
                this.form.enable({ emitEvent: false });
        }));

        const igpsInfoObservable = this.inventoryService.CurrentIgpsInfo.pipe(
            filter(info => !!info),
            tap(info => {
                this.invCountIgpsInfo = info;
                this.setFormValues(info);
            }));

        this.subscriptions = [
            contextObservable
                .pipe(this.activityService.Wait('IGPS Misc Info'))
                .subscribe(),

            igpsInfoObservable.subscribe(),

            this.form.valueChanges
                .pipe(saveDebounce(this.shutdown))
                .subscribe(_ => this.saveIgpsMiscInfo())
        ];
    }

    ngOnDestroy() {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    setFormValues(igpsInfo: InvCountIgpsInfo): void {
        this.form.setValue({
            palletsWashed: igpsInfo.palletsWashed,
            noScanPalletsWashed: igpsInfo.noScanPalletsWashed
        }, {
            emitEvent: false
        });
    }

    getFormValues(): InvCountIgpsInfo {
        const update: InvCountIgpsInfo = { ...this.invCountIgpsInfo };
        update.palletsWashed = this.form.controls.palletsWashed.value;
        return update;
    }

    saveIgpsMiscInfo() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Update iGPS Misc. Info',
                detail: 'Fields are required.'
            });
        } else {
            this.inventoryService.updateInvCountIgpsInfo(this.getFormValues()).subscribe(_ => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Saved iGPS Misc Info',
                    detail: ''
                });
            });
        }
    }
}
